// CANCEL-002 — "Cancellation requested" card.
//
// Rendered under the status area on the order detail page once the engineer
// has sent a cancellation request. Replaces the Cancel order entry card.
// The "Branch confirmed? Mark as cancelled" button is wired up in CANCEL-004.
//
// Ported from octopus-mock/components/orders/cancel-request.tsx
// (CancelRequestCard).
import { useEffect, useRef } from 'react'
import { Phone } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '../../radix'

interface CancellationRequest {
  reasonLabel: string
  note: string
  sentAt: string
  cc: string
  confirmed: boolean
}

interface CancelRequestCardProps {
  request: CancellationRequest
  supplierName: string
  branchName: string
  branchPhone: string
  focusOnMount?: boolean
  // Optional in CANCEL-002; wired in CANCEL-004.
  onMarkCancelled?: () => void
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

function formatSentAt(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${d.getDate()} ${MONTHS[d.getMonth()]}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

export function CancelRequestCard({
  request,
  supplierName,
  branchName,
  branchPhone,
  focusOnMount,
  onMarkCancelled,
}: CancelRequestCardProps) {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const { date, time } = formatSentAt(request.sentAt)

  useEffect(() => {
    if (focusOnMount) headingRef.current?.focus()
  }, [focusOnMount, request.confirmed])

  return (
    <section
      aria-labelledby="cancellation"
      className="mt-3 sm:mt-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
    >
      <div
        className={clsx(
          'px-3 sm:px-4 py-2.5 border-b',
          request.confirmed
            ? 'bg-gray-100 border-gray-200'
            : 'bg-amber-50 border-amber-100'
        )}
      >
        <h2
          id="cancellation"
          ref={headingRef}
          tabIndex={-1}
          className={clsx(
            'font-bold text-xs sm:text-sm outline-none',
            request.confirmed ? 'text-gray-700' : 'text-amber-800'
          )}
        >
          {request.confirmed ? 'Cancellation confirmed' : 'Cancellation requested'}
        </h2>
      </div>
      <div className="px-3 sm:px-4 py-3 space-y-2.5 text-sm text-gray-700 break-words">
        {request.confirmed && (
          <p className="font-medium text-gray-900">
            Cancelled. The branch confirmed this order is cancelled.
          </p>
        )}
        <p>
          Emailed {supplierName} {branchName} on {date} at {time}. We copied
          you in at <span className="break-all">{request.cc}</span>.
        </p>
        <dl className="space-y-1.5">
          <div>
            <dt className="text-xs text-gray-500">Reason</dt>
            <dd className="font-medium text-gray-900">{request.reasonLabel}</dd>
          </div>
          {request.note && (
            <div>
              <dt className="text-xs text-gray-500">Note</dt>
              <dd className="whitespace-pre-line font-medium text-gray-900 break-words">
                {request.note}
              </dd>
            </div>
          )}
        </dl>
        {!request.confirmed && (
          <>
            <p>
              The order is still live until the branch confirms. If it&apos;s
              urgent, call them.
            </p>
            {branchPhone && (
              <Button asChild variant="outline" className="min-h-[44px] w-full">
                <a href={`tel:${branchPhone}`}>
                  <Phone className="w-4 h-4" />
                  Call branch
                  <span className="font-normal tabular-nums">{branchPhone}</span>
                </a>
              </Button>
            )}
            {onMarkCancelled && (
              <Button
                variant="outline"
                className="min-h-[44px] w-full"
                onClick={onMarkCancelled}
              >
                Branch confirmed? Mark as cancelled
              </Button>
            )}
          </>
        )}
      </div>
    </section>
  )
}
